import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import {
  isPortalSyncNotConfiguredError,
  useGetPortalUsageLimitConfigQuery,
  useUpdatePortalUsageLimitConfigMutation,
} from '../endpoints/portal-users.endpoints';

interface UsageLimitConfigEditorProps {
  disabled?: boolean;
}

export function UsageLimitConfigEditor({ disabled = false }: UsageLimitConfigEditorProps) {
  const { data, isLoading, isError, error } = useGetPortalUsageLimitConfigQuery();
  const [updateConfig, { isLoading: isSaving }] = useUpdatePortalUsageLimitConfigMutation();
  const [value, setValue] = useState('');
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (data?.monthly_creative_limit != null) {
      setValue(String(data.monthly_creative_limit));
    }
  }, [data?.monthly_creative_limit]);

  if (isPortalSyncNotConfiguredError(error)) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="mb-6 flex items-center gap-2 text-[#5E6278]">
        <Loader2
          className="size-4 animate-spin"
          aria-hidden
        />
        <span className={portalDs.caption}>
          Loading monthly creative limit…
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <p className={cn(portalDs.caption, 'mb-6 text-[#DC2626]')}>
        Could not load the global monthly creative limit.
      </p>
    );
  }

  const handleSave = async() => {
    setInlineError(null);
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
      setInlineError('Monthly creative limit must be an integer of at least 1.');
      return;
    }

    try {
      await updateConfig(parsed).unwrap();
    } catch (mutationError: unknown) {
      const responseData = (mutationError as { responseData?: Record<string, unknown> })?.responseData;
      const detail = typeof responseData?.detail === 'string'
        ? responseData.detail
        : 'Failed to save monthly creative limit.';
      setInlineError(detail);
    }
  };

  return (
    <div className="mb-6 rounded-xl border border-[#EDEEF2] bg-white p-4 shadow-[0_1px_2px_rgba(13,15,26,0.05)]">
      <label
        htmlFor="monthly-creative-limit"
        className="block text-[13px] font-semibold text-[#0D0F1A]"
      >
        Monthly creative limit
      </label>
      <p className={cn(portalDs.caption, 'mt-1')}>
        Global default applied when a user has no per-user quota override.
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          id="monthly-creative-limit"
          type="number"
          min={1}
          step={1}
          value={value}
          disabled={disabled || isSaving}
          onChange={(event) => setValue(event.target.value)}
          className="h-9 w-full max-w-[160px] rounded-lg border border-[#D9DBE3] px-3 text-[13px] text-[#0D0F1A] focus:border-[#0D0F1A] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        />
        <button
          type="button"
          disabled={disabled || isSaving}
          onClick={() => void handleSave()}
          className="inline-flex h-9 items-center justify-center rounded-lg bg-[#0D0F1A] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2A2D3E] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : 'Save'}
        </button>
      </div>
      {inlineError ? (
        <p className={cn(portalDs.caption, 'mt-2 text-[#DC2626]')}>
          {inlineError}
        </p>
      ) : null}
    </div>
  );
}
